import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TablePagination,
  Skeleton,
} from "@mui/material";
import { getAllPackages } from "../../api/packageAPI";
import { useNavigate } from "react-router-dom";
import usePermissions from "../../hooks/UsePermissions";

const TotalPackage = () => {
  const [filteredPackages, setFilteredPackages] = useState([]);
  const [page, setPage] = useState(0);
  const rowsPerPage = 5;
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const getPermission = usePermissions();

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        const response = await getAllPackages();
        const sortedData = response.data.sort(
          (a, b) => new Date(b.created_at) - new Date(a.created_at)
        );
        setFilteredPackages(sortedData);
      } catch (error) {
        console.error("Error fetching packages:", error);
      } finally {
        setTimeout(()=> setLoading(false),1000);
      }
    };
    fetchPackages();
  }, []);

  // Get unique locations dynamically
  const locations = [...new Set(filteredPackages.map((pkg) => pkg.location))];


  // Prepare summary data dynamically
  const summaryData = [
    { label: "Total", count: filteredPackages.length, location: "" },
    ...locations.map((loc) => ({
      label: loc,
      count: filteredPackages.filter((pkg) => pkg.location === loc).length,
      location: loc,
    })),
  ];

  // Navigate dynamically based on row clicked
  const handleNavigate = (zone) => {
    if (!zone) {
      navigate("/packages"); // Total row, no filter
    } else {
      navigate(`/packages/${encodeURIComponent(zone)}`);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  }


  const renderSkeltonRows = () => {
    return Array.from({ length: rowsPerPage }).map((_, i) => (
      <TableRow key={i}>
        <TableCell align="center">
          <Skeleton variant="text" width={100} height={25} />
        </TableCell>
        <TableCell align="center">
          <Skeleton variant="text" width={60} height={25} />
        </TableCell>
        <TableCell align="center">
          <Skeleton variant="rectangular" width={120} height={35} sx={{ borderRadius: 1 }} />
        </TableCell>
      </TableRow>
    ));
  };


  return (
    getPermission('packages', 'view') && (
      <Box mb={5}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
          Packages Summary
        </Typography>

        <TableContainer component={Paper} sx={{ borderRadius: "12px", boxShadow: 3 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>Zone</TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>Count</TableCell>
                <TableCell align="center" sx={{ fontWeight: "bold" }}>Action</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? renderSkeltonRows()
                : summaryData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item, index) => (
                    <TableRow
                      key={index}
                      hover
                      sx={{
                        "&:hover": { backgroundColor: "#f9f9f9" },
                        transition: "background-color 0.2s ease",
                      }}
                    >
                      <TableCell align="center">{item.label}</TableCell>
                      <TableCell align="center">
                        <Typography fontWeight="bold">{item.count}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Button
                          variant="contained"
                          size="small"
                          color="primary"
                          onClick={() => handleNavigate(item.location)}
                        >
                          View Packages
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>

          {!loading && (
            <TablePagination
              component="div"
              count={summaryData.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[5]} 
            />
          )}

        </TableContainer>
      </Box>
    )

  );
};

export default TotalPackage;
